import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, TextField, Box } from "@mui/material";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import ArgonSelect from "components/ArgonSelect";
import { getParentCategories } from "services/categoriesService";
import {
  addSubCategory,
  updateSubCategory,
} from "services/subCategoriesService";
import { toast } from "react-toastify";

const SubCategoryModal = ({ open, onClose, category, onSuccess }) => {
  const [name, setName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  // Check if a category was passed from the parent (for context menu creation)
  const isCategoryFromContext = category && category.name && category.value;

  useEffect(() => {
    const fetchParentCategories = async () => {
      // Only fetch categories if no category was passed from context menu
      if (!isCategoryFromContext) {
        const response = await getParentCategories();
        if (response.success) {
          setCategories(response.data);
        }
      }
    };
    fetchParentCategories();
  }, [isCategoryFromContext]);

  const isEditMode = !!category && !isCategoryFromContext;

  useEffect(() => {
    if (category) {
      if (isCategoryFromContext) {
        // Category passed from context menu - auto-populate the select field
        setName("");
        setSelectedCategory({
          label: category.name,
          value: category.value,
        });
      } else {
        // Edit mode - populate with existing subcategory data
        setName(category.name);
        setSelectedCategory(category.parent_id);
      }
    } else {
      setName("");
      setSelectedCategory("");
    }
  }, [category, open, isCategoryFromContext]);

  const handleSubmit = async () => {
    if (!name || !selectedCategory) {
      toast.error("Both sub-category name and main category are required.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name,
        parent_id: selectedCategory.value,
      };

      if (isEditMode) {
        await updateSubCategory(category._id, payload);
      } else {
        await addSubCategory(payload);
      }

      setLoading(false);
      onSuccess();
      onClose();
    } catch (err) {
      setLoading(false);
      toast.error(err.response.data.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      disableBackdropClick
      PaperProps={{
        style: {
          borderRadius: "16px",
          padding: "40px 32px",
          textAlign: "center",
          backgroundColor: "#ffffff",
        },
      }}
    >
      <DialogContent>
        <ArgonTypography variant="h4" mb={3}>
          {isEditMode
            ? "Edit Sub Category"
            : isCategoryFromContext
            ? `Add New Sub Category to "${category.name}"`
            : "Add New Sub Category"}
        </ArgonTypography>

        <Box mb={3} textAlign="left">
          <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
            Sub Category Title
          </ArgonTypography>
          <TextField
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter Sub Category Title"
            disabled={loading}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#fff",
              },
              "& input::placeholder": {
                color: "#94A3B8",
                fontSize: 14,
              },
              "& .MuiInputBase-input": {
                fontSize: 14,
                fontWeight: 500,
                color: "#172a4d",
              },
            }}
          />
        </Box>

        <Box mb={3} textAlign="left">
          <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
            Main Category
          </ArgonTypography>
          <ArgonSelect
            options={
              isCategoryFromContext && selectedCategory
                ? [selectedCategory]
                : categories.map((category) => ({
                    label: category.name,
                    value: category._id,
                  }))
            }
            value={selectedCategory}
            onChange={(selectedValue) => {
              setSelectedCategory(selectedValue);
            }}
            placeholder="Select Main Category"
            disabled={loading || isCategoryFromContext}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#fff",
              },
              "& input::placeholder": {
                color: "#94A3B8",
                fontSize: 14,
              },
              "& .MuiInputBase-input": {
                fontSize: 14,
                fontWeight: 500,
                color: "#172a4d",
              },
            }}
          />
        </Box>

        <Box display="flex" justifyContent="center" gap={2}>
          <ArgonButton
            variant="outlined"
            color="secondary"
            onClick={onClose}
            disabled={loading}
            sx={{ minWidth: 100, backgroundColor: "#ffff", color: "#2d3f50" }}
          >
            Cancel
          </ArgonButton>
          <ArgonButton
            variant="contained"
            color="#2d3f50"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ minWidth: 100, backgroundColor: "#2d3f50", color: "#ffff" }}
          >
            {loading ? "Saving..." : isEditMode ? "Save" : "Add"}
          </ArgonButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SubCategoryModal;
